import { useState } from 'react';
import { ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO = "'IBM Plex Mono', monospace";

const FAQS = [
  {
    category: 'Platform & Product',
    items: [
      {
        q: 'What is FashionSpyder?',
        a: 'FashionSpyder is an AI-powered HR intelligence platform built exclusively for the fashion industry. It helps HR directors and talent acquisition managers at fashion houses discover, evaluate, and hire verified creative professionals from the Flence community. Think of it as a precision search engine for fashion talent — combining AI matchmaking, aesthetic analysis, and a streamlined hiring pipeline.',
      },
      {
        q: 'How is FashionSpyder different from LinkedIn or traditional job boards?',
        a: 'FashionSpyder is not a job board. It\'s an intelligence layer. Every profile is pre-verified by Flence with real portfolio work, skills validation, and availability status. Our Career Visibility Engine evaluates candidates across 4 professional dimensions — not just keywords. You search talent that is already quality-filtered and fashion-specific, not the entire internet.',
      },
      {
        q: 'Who can access FashionSpyder?',
        a: 'FashionSpyder is available exclusively to HR professionals and hiring teams at fashion brands, agencies, and studios. Registration requires a verified company email address. Personal email providers (Gmail, Yahoo, Hotmail, etc.) are not accepted. You can create an account in minutes and choose a plan to unlock the full platform.',
      },
      {
        q: 'Where does the talent pool come from?',
        a: 'All candidates on FashionSpyder are professionals who have built verified profiles on Flence.com — a dedicated portfolio and career platform for the fashion industry. Flence handles candidate onboarding, profile validation, and portfolio review. FashionSpyder reads this data with candidate consent and applies our Career Visibility analysis.',
      },
    ],
  },
  {
    category: 'Scoring & AI',
    items: [
      {
        q: 'How does the Career Visibility Engine work?',
        a: 'Each candidate receives a Visibility Index from 0–100 built from four weighted pillars: V₁ Craft & Tools (25pts), V₂ Aesthetic Identity (40pts), V₃ Industry Presence (20pts), and V₄ Availability & Fit (15pts). The engine combines profile data, portfolio analysis, and real-time availability to surface the strongest match for any given role.',
      },
      {
        q: 'What is the AI Matchmaker?',
        a: 'The AI Matchmaker (available on the INTELLIGENCE plan) lets you describe a role in natural language — "Senior Womenswear Designer, 6+ years, Milan, Luxury RTW" — and returns a ranked shortlist from the full candidate pool. It uses semantic understanding to match not just keywords, but aesthetic intent, cultural fit, and logistical alignment. Results include an explanation of why each candidate was selected.',
      },
      {
        q: 'Can I adjust how candidates are ranked?',
        a: 'Yes. On the INTELLIGENCE plan you can adjust the weighting sliders in the AI Matchmaker to prioritise different dimensions — for example, weighting aesthetic identity higher than logistics for a creative director search, vs. the inverse for a production role.',
      },
      {
        q: 'What is the "Find Similar" feature?',
        a: '"Find Similar" uses the profile of any candidate you select as a reference point to discover other professionals with a matching signature — aesthetically, professionally, and logistically. It is available on the INTELLIGENCE plan.',
      },
      {
        q: 'Are the StyleDNA and AI scores always accurate?',
        a: 'Our StyleDNA, Visibility Index, role-fit scores and AI Analyst responses are generated by FashionSpyder\'s own proprietary algorithm combined with AI. They are built to simplify and speed up your team\'s work — but AI can make mistakes, and results may be incomplete or inaccurate. These insights are decision-support tools only: they are meant to assist, not replace, human judgement, and every output should be reviewed and verified by your hiring team before any decision. They must not be used as the sole basis for a hiring or rejection decision.',
      },
    ],
  },
  {
    category: 'Plans & Billing',
    items: [
      {
        q: 'What is the difference between RECON and INTELLIGENCE?',
        a: 'RECON (€149/month) gives you full access to the talent gallery, basic filters (role, seniority, city), complete candidate profiles, the Kanban pipeline, and the HR dashboard. INTELLIGENCE (€349/month) adds the AI Matchmaker, AI Talent Brief, advanced filters, the AI DNA Intelligence Panel on each profile, Compare & Decide, Find Similar, and priority support.',
      },
      {
        q: 'Can I cancel anytime?',
        a: 'Yes. You can cancel your subscription at any time from your Account dashboard. Your access continues until the end of your current billing period. We do not offer prorated refunds for partial months, but we will never charge you after cancellation.',
      },
      {
        q: 'Is there an annual plan?',
        a: 'Yes. Annual plans are available for both RECON and INTELLIGENCE, at approximately 30% savings versus monthly billing. RECON Annual is €1,252/year (≈ €104/month). INTELLIGENCE Annual is €2,930/year (≈ €244/month).',
      },
      {
        q: 'What is the White Glove hiring service?',
        a: 'White Glove is a fully managed, dedicated hiring service for companies that need a hands-on search partner. Our team of fashion talent specialists handles the entire process — from brief to placement. The service is available to accepted companies by application only. Pricing is disclosed after your brief has been reviewed and accepted by our team. No commitment is required to enquire.',
      },
    ],
  },
  {
    category: 'Candidates & Privacy',
    items: [
      {
        q: 'Can candidates see which companies have viewed their profile?',
        a: 'No. FashionSpyder operates a confidential search model by default. Candidates are not notified of individual profile views. This is intentional — many companies are searching discreetly, including for roles they have not yet publicly advertised.',
      },
      {
        q: 'Can I contact candidates directly through FashionSpyder?',
        a: 'Yes. INTELLIGENCE subscribers can initiate a conversation with any candidate via FashionSpyder\'s internal messaging system. Messages are tracked and stored within your account, and delivered to the candidate through their Flence notification system. Direct contact details (email, phone) are not shared.',
      },
      {
        q: 'Is candidate data GDPR compliant?',
        a: 'Yes. All candidate data is sourced from Flence, where candidates have explicitly consented to their profiles being made available to verified HR partners. FashionSpyder acts as a data processor and displays only what candidates have chosen to share publicly on their Flence profile. See our Privacy Policy for full details.',
      },
      {
        q: 'What happens when I mark a candidate as hired?',
        a: 'When you move a candidate to "Hired" in your pipeline, FashionSpyder notifies Flence. Flence then contacts the candidate to confirm the hire and update their availability status — ensuring the directory remains accurate and that other companies do not contact a candidate who is no longer available.',
      },
    ],
  },
  {
    category: 'Technical',
    items: [
      {
        q: 'Which browsers and devices are supported?',
        a: 'FashionSpyder is a web application optimised for modern desktop browsers: Chrome, Safari, Firefox, and Edge. Mobile browsing is supported for gallery viewing but the full pipeline and matchmaker experience is designed for desktop.',
      },
      {
        q: 'Is my account data secure?',
        a: 'Yes. Authentication uses two-factor verification (2FA) for all login events. Sessions use secure, HTTP-only tokens. All data is stored in an EU-hosted database (Supabase, with EU region). Payments are processed by Stripe — we never store card details. See our Privacy Policy for full technical details.',
      },
      {
        q: 'How do I get technical support?',
        a: 'Logged-in users can submit a support ticket directly from the dashboard (Dashboard → Support). We respond to technical issues within 12 hours. For billing or account queries, response time is within 24 hours.',
      },
    ],
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-0">
      <button
        className="w-full flex items-start justify-between gap-4 py-5 text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="font-medium text-foreground leading-snug pr-4">{q}</span>
        <ChevronRight
          className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5 transition-transform"
          style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}
        />
      </button>
      {open && (
        <p className="text-sm text-muted-foreground leading-relaxed pb-5 pr-8">{a}</p>
      )}
    </div>
  );
};

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState(null);

  const displayed = activeCategory
    ? FAQS.filter(g => g.category === activeCategory)
    : FAQS;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="FAQ — Frequently Asked Questions"
        description="Everything you need to know about FashionSpyder — how the AI works, pricing plans, candidate data privacy, GDPR compliance, and how to get started with fashion talent acquisition."
        path="/faq"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.flatMap(group =>
            group.items.map(item => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a },
            }))
          ),
        }}
      />
      {/* Hero */}
      <section className="py-20 lg:py-24 px-6 lg:px-20 border-b border-border">
        <div className="max-w-[900px] mx-auto">
          <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground" style={{ fontFamily: MONO }}>
            Support
          </span>
          <h1 className="text-5xl lg:text-6xl font-semibold mt-4 mb-4 tracking-tight" style={{ fontFamily: SERIF }}>
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl">
            Everything you need to know about FashionSpyder — from how the AI works to billing, privacy, and candidate data.
          </p>
        </div>
      </section>

      {/* Category filters */}
      <section className="px-6 lg:px-20 py-8 border-b border-border bg-[hsl(var(--secondary))]">
        <div className="max-w-[900px] mx-auto flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${!activeCategory ? 'bg-foreground text-background' : 'border border-border text-muted-foreground hover:border-foreground hover:text-foreground'}`}
            style={{ fontFamily: MONO }}
          >
            All Topics
          </button>
          {FAQS.map(g => (
            <button
              key={g.category}
              onClick={() => setActiveCategory(activeCategory === g.category ? null : g.category)}
              className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${activeCategory === g.category ? 'bg-foreground text-background' : 'border border-border text-muted-foreground hover:border-foreground hover:text-foreground'}`}
              style={{ fontFamily: MONO }}
            >
              {g.category}
            </button>
          ))}
        </div>
      </section>

      {/* FAQ items */}
      <section className="px-6 lg:px-20 py-16">
        <div className="max-w-[900px] mx-auto space-y-12">
          {displayed.map(group => (
            <div key={group.category}>
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-6" style={{ fontFamily: MONO }}>
                {group.category}
              </p>
              <div className="border border-border bg-white px-6">
                {group.items.map(item => (
                  <FAQItem key={item.q} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Still have questions */}
      <section className="px-6 lg:px-20 py-16 bg-[hsl(var(--secondary))] border-t border-border">
        <div className="max-w-[900px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-border bg-white p-6">
            <h3 className="font-semibold mb-2">For HR teams</h3>
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
              Have a question not covered here? Submit a support ticket from your dashboard or contact us directly.
            </p>
            <Link to="/dashboard/support" className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-[hsl(var(--primary))] transition-colors">
              Open a support ticket <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="border border-border bg-white p-6">
            <h3 className="font-semibold mb-2">For fashion professionals</h3>
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
              Learn how FashionSpyder evaluates your profile and how to maximise your visibility to hiring teams.
            </p>
            <Link to="/for-talent" className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-[hsl(var(--primary))] transition-colors">
              Visit the Talent Hub <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
